import React, { useMemo, useState } from 'react';
import { Box, Typography, Card, Tabs, Tab, Link as MuiLink } from '@mui/material';
import { useSearchParams, useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';

import styles from './MeterDetails.styles';

import CenteredLoader from '../../../../components/loaders/PageLoader';
import BreadcrumbTrail from '../../../../layouts/BreadcrumbTrail';
import DateRangePicker from '../../../../components/filters/DateRangePicker';

import MeterDetailsSummaryCard from '../../components/MeterDetails/MeterDetailsSummaryCard';
import MeterDetailsConsumptionChart, { MeterDetailsConsumptionLegend } from '../../components/MeterDetails/MeterDetailsConsumptionChart';
import MeterDetailsQualityChart, { MeterDetailsQualityLegend } from '../../components/MeterDetails/MeterDetailsQualityChart';
import MeterUploadCalendar from '../../components/MeterDetails/MeterUploadCalendar';
import MeterDetailsTable from '../../components/MeterDetails/MeterDetailsTable';

import { getMeterTypeName } from '../../utils/meter.helpers';
import { makeDefaultDateRange21 } from '../../utils/dates';
import { useMeterDetails } from '../../hooks/useMeterDetails';

import {
  dash,
  fmtDT,
  deriveInitialTab,
  updateTabInSearch,
  computeStatusByDate,
  initFirstOfMonthUTC,
  defaultRelativeRange,
} from '../../utils/meterDetails.helpers';

import { useCrumb, useNavWithTrail } from '../../../../layouts/TrailProvider';

export default function MeterDetails() {
  const { isSidebarExpanded } = useOutletContext();
  const { meterNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { unit: unitFromNav, evn: evnFromNav } = location.state || {};
  const nav = useNavWithTrail(navigate);
  const [params] = useSearchParams();

  useCrumb(`Meter ${meterNumber}`, `/home/meters/${encodeURIComponent(meterNumber)}`);

  const unitFromQuery = params.get('unit');
  const evnFromQuery = params.get('evn');
  const meterContext = {
    unit: unitFromQuery ?? unitFromNav,
    evn: evnFromQuery ?? evnFromNav,
  };
  const [tab, setTab] = useState(deriveInitialTab(params));
  const handleTab = (_e, v) => updateTabInSearch(params, setTab, v, navigate, location);

  const { meterData, files, loading: baseLoading } =
    useMeterDetails(meterNumber, meterContext);
  const unitFromApi = meterData?.Unit ?? meterData?.unit ?? meterData?.unitCode ?? null;
  const evnFromApi = meterData?.EVN ?? meterData?.evn ?? meterData?.Evn ?? null;

  const unit = meterContext.unit ?? unitFromApi ?? null;
  const evn = meterContext.evn ?? evnFromApi ?? null;

  const [dateRange, setDateRange] = useState(makeDefaultDateRange21());
  const [consumptionRange, setConsumptionRange] = useState(makeDefaultDateRange21());

  const meterType = Number(meterData?.MeterType) || 1;

  const [viewDate, setViewDate] = useState(initFirstOfMonthUTC);
  const [selectedDate, setSelectedDate] = useState(null);

  const statusByDate = useMemo(() => computeStatusByDate(files ?? []), [files]);
  const { rangeStart, rangeEnd } = useMemo(() => defaultRelativeRange(20), []);
  const ready = !baseLoading;

  return (
    <Box
      sx={{
        ...styles.meterDetails,
        marginLeft: isSidebarExpanded ? '240px' : '95px',
        width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
        transition: 'all 0.3s ease',
      }}
    >
      {!ready ? (
        <CenteredLoader />
      ) : (
        <>
          <BreadcrumbTrail />

          <Box sx={styles.headerRow}>
            <Typography sx={styles.pageTitle}>{dash(meterNumber)}</Typography>
          </Box>

          <Box sx={styles.versionHistoryContainer}>
            <Typography variant="body2" sx={styles.versionInfoText}>
              Version history{' '}
              <MuiLink
                underline="none"
                sx={styles.versionLink}
                onClick={() => nav(`/home/meters/${encodeURIComponent(meterNumber)}/history`)}
              >
                click here
              </MuiLink>
            </Typography>
          </Box>

          <Box sx={styles.detailsContent}>
            <MeterDetailsSummaryCard
              meterData={meterData}
              dash={dash}
              fmtDT={fmtDT}
              getMeterTypeName={getMeterTypeName}
              unit={unit}
              evn={evn}
            />

            <Card sx={styles.tabbedCard}>
              <Box sx={styles.cardTabsHeader}>
                <Tabs
                  value={tab}
                  onChange={handleTab}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={styles.cardTabsBar}
                >
                  <Tab value="uploads" label="Upload Activity" />
                  <Tab value="quality" label="Data Quality" />
                  <Tab value="consumption" label="Energy Consumption" />
                </Tabs>

                {(tab === 'quality' || tab === 'consumption') && (
                  <Box sx={styles.headerControls}>
                    {tab === 'quality' && (
                      <>
                        <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
                        <MeterDetailsQualityLegend />
                      </>
                    )}
                    {tab === 'consumption' && (
                      <>
                        <DateRangePicker
                          dateRange={consumptionRange}
                          setDateRange={setConsumptionRange}
                        />
                        <MeterDetailsConsumptionLegend meterType={meterType} />
                      </>
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={styles.tabbedCardBody}>
                {tab === 'uploads' && (
                  <Box sx={styles.uploadsGrid}>
                    <Box sx={styles.uploadsCalendarBox}>
                      <MeterUploadCalendar
                        statusByDate={statusByDate}
                        viewDate={viewDate}
                        setViewDate={setViewDate}
                        selectedDate={selectedDate}
                        onDayClick={(iso) => setSelectedDate((prev) => (prev === iso ? null : iso))}
                        unit={unit}
                        evn={evn}
                      />
                    </Box>

                    <Box sx={styles.uploadsDivider} />

                    <Box sx={styles.uploadsTableBox}>
                      <MeterDetailsTable
                        files={files}
                        viewDate={viewDate}
                        setViewDate={setViewDate}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        unit={unit}
                        evn={evn}
                      />
                    </Box>
                  </Box>
                )}

                {tab === 'quality' && (
                  <Box sx={styles.chartPane}>
                    <MeterDetailsQualityChart
                      meterNumber={meterNumber}
                      startDate={dateRange.start}
                      endDate={dateRange.end}
                      unit={unit}
                      evn={evn}
                    />
                  </Box>
                )}

                {tab === 'consumption' && (
                  <Box sx={styles.chartPane}>
                    <MeterDetailsConsumptionChart
                      meterNumber={meterNumber}
                      startDate={consumptionRange.start ?? rangeStart}
                      endDate={consumptionRange.end ?? rangeEnd}
                      meterType={meterType}
                      dateRange={consumptionRange}
                      unit={unit}
                      evn={evn}
                    />
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
}
